import { useEffect, useRef, useState } from 'react';
import { Check, RotateCcw } from 'lucide-react';
import { cn } from '@/shared/utils/cn';
import type { ListingFilter } from '../../domain/models/Filter';

/** Max filter items shown per category before the "+ N more" expander appears. */
const MAX_VISIBLE_ITEMS = 6;

interface PlpDesktopFilterSidebarProps {
  filters: ListingFilter[];
  selectedOptions: Record<string, string[]>;
  filtersResetUrl?: string;
  isPendingNavigation?: boolean;
  isStorePlp?: boolean;
  onToggleOption: (category: string, name: string, url: string) => void;
  onReset: () => void;
  /** Analytics: fires when a filter item is toggled; receives the full post-toggle selection payload ({ category: 'displayName1,displayName2' }) */
  onFilterItemToggle?: (payload: Record<string, string>) => void;
  /** When sort changes, scroll the filter list back to the top. */
  currentSortBy?: string;
}

export function PlpDesktopFilterSidebar({
  filters,
  selectedOptions,
  isPendingNavigation = false,
  isStorePlp = false,
  onToggleOption,
  onReset,
  onFilterItemToggle,
  currentSortBy,
}: PlpDesktopFilterSidebarProps) {
  const [expandedCategories, setExpandedCategories] = useState<Set<string>>(new Set());
  const scrollRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    scrollRef.current?.scrollTo({ top: 0, behavior: 'auto' });
  }, [currentSortBy]);

  //hiding reset button for now, to be removed later
  // const totalActiveCount = Object.values(selectedOptions).reduce(
  //   (sum, names) => sum + names.length,
  //   0
  // );
  // const canReset = totalActiveCount > 0;

  const canReset = false;
  
  function toggleExpanded(category: string) {
    setExpandedCategories((prev) => {
      const next = new Set(prev);
      if (next.has(category)) {
        next.delete(category);
      } else {
        next.add(category);
      }
      return next;
    });
  }

  return (
    <aside className="hidden w-[270px] shrink-0 md:flex md:flex-col h-full">
      {/* Header — always visible, never scrolls */}
      <div
        className="flex shrink-0 items-center justify-between rounded-t-md border border-gray-200 px-4 py-3"
          style={{ backgroundColor: isStorePlp ? 'var(--store-plp-top-bar-bg)' : 'var(--plp-top-bar-bg, #6b9dc4)' }}
        >
          <h2 className="text-[13px] font-bold uppercase tracking-widest text-white">
            Filters{isPendingNavigation ? '…' : ''}
          </h2>
          {canReset && (
            <button
              type="button"
              aria-label="Reset all filters"
              className="flex items-center gap-1 text-[11px] font-semibold uppercase tracking-wider text-white/80 hover:text-white"
              onClick={() => {
                onReset();
                scrollRef.current?.scrollTo({ top: 0, behavior: 'auto' });
              }}
            >
              <RotateCcw className="h-3 w-3" aria-hidden />
              Reset
            </button>
          )}
      </div>

      {/* Filter categories — scrollable */}
      <div
        ref={scrollRef}
        className="flex-1 overflow-y-auto overscroll-contain divide-y divide-gray-200 border border-t-0 border-gray-200 [&::-webkit-scrollbar]:w-[5px] [&::-webkit-scrollbar-thumb]:rounded-[5px] [&::-webkit-scrollbar-thumb]:bg-[#ddd] [&::-webkit-scrollbar-thumb]:outline-none"
      >
          {filters.map((filter) => {
            const selectedInCategory = selectedOptions[filter.category] ?? [];
            const isExpanded = expandedCategories.has(filter.category);
            const visibleItems =
              isExpanded || filter.items.length <= MAX_VISIBLE_ITEMS
                ? filter.items
                : filter.items.slice(0, MAX_VISIBLE_ITEMS);
            const hiddenCount = filter.items.length - MAX_VISIBLE_ITEMS;

            return (
              <div key={filter.category} className="px-4 py-3">
                {/* Category label */}
                <p className="mb-2 text-[13px] font-bold text-gray-900">{filter.category}</p>

                {/* Items */}
                <ul className="space-y-1">
                  {visibleItems.map((item) => {
                    const isChecked = selectedInCategory.includes(item.name);
                    return (
                      <li key={item.name}>
                        <button
                          type="button"
                          className="flex w-full items-center gap-2.5 py-1 text-left"
                          onClick={() => {
                            if (onFilterItemToggle) {
                              // Build post-toggle selectedOptions
                              const nextSelected: Record<string, string[]> = {};
                              for (const [cat, names] of Object.entries(selectedOptions)) {
                                if (cat !== filter.category) nextSelected[cat] = names;
                              }
                              const nextNames = isChecked
                                ? selectedInCategory.filter((n) => n !== item.name)
                                : [...selectedInCategory, item.name];
                              if (nextNames.length > 0) nextSelected[filter.category] = nextNames;
                              // Map names → displayNames, build payload same as mobile Apply
                              const payload: Record<string, string> = {};
                              for (const [cat, names] of Object.entries(nextSelected)) {
                                const f = filters.find((fi) => fi.category === cat);
                                payload[cat] = names
                                  .map((n) => f?.items.find((i) => i.name === n)?.displayName ?? n)
                                  .join(',');
                              }
                              onFilterItemToggle(payload);
                            }
                            onToggleOption(filter.category, item.name, item.url);
                            scrollRef.current?.scrollTo({ top: 0, behavior: 'smooth' });
                          }}
                        >
                          {/* Square checkbox */}
                          <span
                            className={cn(
                              'flex h-4 w-4 shrink-0 items-center justify-center border rounded-sm',
                              isChecked ? 'border-[--plp-top-bar-bg] bg-[--plp-top-bar-bg]' : 'border-gray-400 bg-white'
                            )}
                            style={isChecked && isStorePlp ? {
                              backgroundColor: 'var(--store-plp-top-bar-bg)',
                              borderColor: 'var(--store-plp-top-bar-bg)',
                            } : undefined}
                            aria-hidden
                          >
                            {isChecked && (
                              <Check className="h-2.5 w-2.5 text-white" strokeWidth={3} />
                            )}
                          </span>
                          <span
                            className={cn(
                              'text-[13px] leading-snug',
                              isChecked ? 'font-semibold text-gray-900' : 'text-gray-700'
                            )}
                          >
                            {item.displayName}
                            {item.count > 0 && (
                              <span className="font-normal text-gray-500"> ({item.count})</span>
                            )}
                          </span>
                        </button>
                      </li>
                    );
                  })}
                </ul>

                {/* "+ N more" / "Show less" toggle */}
                {hiddenCount > 0 && (
                  <button
                    type="button"
                    className="mt-1.5 text-[12px] font-semibold text-[#3F84AD] hover:underline"
                    onClick={() => toggleExpanded(filter.category)}
                  >
                    {isExpanded ? 'Show less' : `+ ${hiddenCount} more`}
                  </button>
                )}
              </div>
            );
          })}
        </div>
    </aside>
  );
}
